import React, {useState} from 'react';
import {useForm} from 'react-hook-form';

import {
  Box, HStack, IconButton, Input, Text, useMultiStyleConfig, VStack,
} from '@salesforce/retail-react-app/app/components/shared/ui';
import SocialIcons from '@salesforce/retail-react-app/app/components/social-icons';

import {ArrowRightIcon, CheckIcon} from '@amblique/king-living/app/components/icons';

export const Newsletter = () => {
  const styles = useMultiStyleConfig('Newsletter', {});

  const [subscriptionStatus, setSubscriptionStatus] = useState({
    isSuccess: false,
    isError: false,
  });

  const {
    register,
    handleSubmit,
    formState: {errors, isSubmitting},
    reset,
  } = useForm();

  const onSubmit = async (data) => {
    try {
      // Simulate newsletter subscription API call
      await new Promise((resolve, reject) => {
        console.log('Newsletter subscription:', data.email);

        // Simulating a random success/failure for demonstration
        setTimeout(() => {
          if (Math.random() > 0.5) {
            // Successful subscription
            setSubscriptionStatus({
              isSuccess: true,
              isError: false,
            });
            reset();
            resolve();
          } else {
            // Subscription failed
            setSubscriptionStatus({
              isSuccess: false,
              isError: true,
            });
            reject(new Error('Subscription failed'));
          }
        }, 1000);
      });
    } catch (error) {
      console.error('Newsletter subscription error:', error);
    }
  };

  // Reset status after a few seconds
  React.useEffect(() => {
    let timeoutId;

    if (subscriptionStatus.isSuccess || subscriptionStatus.isError) {
      timeoutId = setTimeout(() => {
        setSubscriptionStatus({
          isSuccess: false,
          isError: false,
        });
      }, 3000); // Hide message after 3 seconds
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [subscriptionStatus]);

  return (
    <Box sx={styles.container}>
      <VStack align="stretch" spacing={{base: 12, lg: 28}}>
        {/* Header Section */}
        <VStack align="stretch" spacing={3}>
          <Text sx={styles.heading}>
            Stay in the know
          </Text>
          <Text sx={styles.subtitle}>
            Sign up to King Living to hear about new collections, offers and inspiration
          </Text>

          {/* Email Form */}
          <Box as="form" noValidate onSubmit={handleSubmit(onSubmit)}>
            <HStack spacing={0} sx={styles.inputContainer}>
              <Input
                placeholder="Enter your email address"
                sx={styles.input}
                type="email"
                {...register('email', {
                  required: 'Email is required',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Please enter a valid email address',
                  },
                })}
              />
              <IconButton
                aria-label="Subscribe to newsletter"
                isLoading={isSubmitting}
                sx={styles.submitButton}
                type="submit"
                variant="unstyled"
              >
                {subscriptionStatus.isSuccess ? (
                  <CheckIcon {...styles.submitButtonIcon} />
                ) : (
                  <ArrowRightIcon {...styles.submitButtonIcon} />
                )}
              </IconButton>
            </HStack>
            {/* Form Validation Error */}
            {errors.email && (
              <Text mt={2} sx={styles.errorText}>
                {errors.email.message}
              </Text>
            )}
          </Box>
          {!subscriptionStatus.isSuccess && !subscriptionStatus.isError && (
            <Text sx={styles.recaptcha}>
              This site is protected by reCAPTCHA and the <Text as="a" href="https://policies.google.com/privacy" rel="noopener noreferrer" sx={styles.link} target="_blank">Privacy Policy</Text> and <Text as="a" href="https://policies.google.com/terms" rel="noopener noreferrer" sx={styles.link} target="_blank">Terms of Service</Text> apply.
            </Text>
          )}

          {/* Success Message */}
          {subscriptionStatus.isSuccess && (
            <Text sx={styles.recaptcha}>
              Thanks! You’ve subscribed.
            </Text>
          )}

          {/* Error Message */}
          {subscriptionStatus.isError && (
            <Text sx={styles.recaptcha}>
              Something Went Wrong!
            </Text>
          )}
        </VStack>

        {/* Social Media Section */}
        <VStack align="stretch" spacing={4}>
          <Text sx={styles.socialHeading}>
            Follow us for daily updates
          </Text>

          <SocialIcons pinterestInnerColor="black" variant="flex-start" {...styles.socialIcons} />
        </VStack>
      </VStack>
    </Box>
  );
};
